import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FileFilterBody } from '../models/general.models';
import {
  ChildProject,
  Project,
  ProjectManagementResponse
} from '../models/project.module';
import { RiskScores } from '../models/report.model';

@Injectable({
  providedIn: 'root'
})
export class NewProjectService {
  private baseUrl: string = environment.baseUrl;
  public metadataKeys: string[] = [];

  constructor(private http: HttpClient) {}

  public getProjectsByPath(
    path: string,
    state: string,
    sort: string
  ): Observable<Project[]> {
    // Handle empty or undefined path - use root projects endpoint
    // if (!path || path === '' || path === 'root') {
    //   let endpoint = `${this.baseUrl}/projects`;
    //   if (archived) {
    //     endpoint += `?is_archived=${archived}`;
    //   }
    //   return this.http.get<any>(endpoint).pipe(
    //     map((response) => {
    //       // Handle new API response format
    //       if (response.items) {
    //         // New API format - convert to old format
    //         return {
    //           id: 'root',
    //           name: 'Root',
    //           path: 'root',
    //           is_root: true,
    //           children: response.items,
    //           user: { email: '' },
    //           team: { name: '' },
    //           shared_with: [],
    //           is_archived: false,
    //           skipped_paths: [],
    //           description: '',
    //           bounding_box: null,
    //           items: []
    //         } as Project;
    //       }
    //       return response;
    //     })
    //   );
    // }

    // For specific paths, use the path endpoint
    let endpoint: string;
    if (!path || path === '') {
      endpoint = `${this.baseUrl}/projects/by-path/`;
    } else {
      endpoint = `${this.baseUrl}/projects/by-path/${path}`;
    }

    const queryParams: string[] = [];
    if (state === 'root' || state === 'shared') {
      queryParams.push('filters=is_archived:eq:false');
      queryParams.push(`sorts=${sort}`);
    } else if (state === 'archived') {
      queryParams.push('filters=is_archived:eq:true');
      queryParams.push(`sorts=${sort}`);
    }

    if (queryParams.length > 0) {
      endpoint += `?${  queryParams.join('&')}`;
    }

    return this.http.get<Project[]>(endpoint).pipe(map(resp => {
      return resp.filter(item => {
        if (path === '') {
          if (state === 'root') return item.is_owned_by_current_user;
          if (state === 'shared') return !item.is_owned_by_current_user;
        }
        return true;
      });
    }));
  }

  public getShared():Observable<any> {
    const endpoint = `${this.baseUrl}/shares/shared-with-me`;
    return this.http.get<any>(endpoint);
  }
  public deleteShare(id: string): Observable<any> {
    const endpoint = `${this.baseUrl}/shares/${id}`;
    return this.http.delete(endpoint);
  }

  public searchProjects(search: string): Observable<Array<Project>> {
    // Use the new search endpoint from Swagger
    const endpoint = `${this.baseUrl}/projects/search`;
    const searchRequest = {
      query: search || '',
      skip: 0,
      limit: 100
    };
    return this.http.post<any>(endpoint, searchRequest).pipe(
      map((response) => {
        // Handle new API response format
        if (response.items) {
          return response.items;
        }
        return response;
      })
    );
  }

  public getProjectsManagement(queryKeys: {
    [key: string]: any;
  }): Observable<Project[]> {
    let endpoint = `${this.baseUrl}/projects/by-path/`;
    Object.keys(queryKeys).forEach((key, index) => {
      if (queryKeys[key]) {
        const jointer = index === 0 ? '?' : '&';
        endpoint += `${jointer}${key}=${queryKeys[key]}`;
      }
    });
    return this.http.get<Project[]>(endpoint);
  }

  public getProjectManagementById(id: string): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects/${id}`;
    return this.http.get<Project>(endpoint);
  }

  public getProjectActivities(
    id: string,
    type: string,
    filter: FileFilterBody
  ): Observable<ProjectManagementResponse> {
    let endpoint = `${this.baseUrl}/projects/management/${id}/activities/?type=${type}`;
    const jointer = '&';
    Object.keys(filter).forEach((key) => {
      if (key === 'file_format' && filter[key]?.length) {
        endpoint += `${jointer}${key}=${filter[key]}`;
      }
      if (key === 'date' && filter[key]?.from && filter[key]?.to) {
        endpoint += `${jointer}created_after=${filter[key].from.toISOString()}`;
        endpoint += `${jointer}created_before=${filter[key].to.toISOString()}`;
      }
      if (key === 'ownership' && filter[key]?.length) {
        endpoint += `${jointer}user_id=${filter[key]
          .map((user) => user.user_id)
          .join(',')}`;
      }
    });
    return this.http.get<ProjectManagementResponse>(endpoint);
  }

  public getProjectById(id: number | string): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects/${id}`;
    return this.http.get<Project>(endpoint);
  }

  public changeProject(
    id: string,
    body: Partial<Project>
  ): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects/${id}`;
    return this.http.patch<Project>(endpoint, { ...body });
  }

  public deleteProject(id: string): Observable<any> {
    const endpoint = `${this.baseUrl}/projects/${id}`;
    return this.http.delete(endpoint);
  }

  public archiveProject(
    project: ChildProject,
    parentId: string
  ): Observable<any> {
    const type = !project.is_archived ? 'archive' : 'unarchive';
    const endpoint = `${this.baseUrl}/projects/${project.id}/${type}`;
    const body = {
      name: project.name,
      parent: parentId
    };
    return this.http.post(endpoint, body);
  }

  public createProject(body: {
    name: string;
    parent_id?: number | string;
    description?: string;
    tenant_id?: string;
    team_id?: string;
    project_metadata?: any;
  }): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects`;
    return this.http.post<Project>(endpoint, body);
  }

  public shareProject(
    body: { user_id: string | number },
    id: string
  ): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects/${id}/share/`;
    return this.http.post<Project>(endpoint, body);
  }

  public leaveSharedProject(id: string): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects/${id}/leave_shared_project/`;
    return this.http.post<Project>(endpoint, {});
  }

  public removeShareProject(
    body: { user_id: string | number },
    id: string
  ): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects/${id}/remove_share`;
    return this.http.post<Project>(endpoint, body);
  }

  public getAreaMetadataKeys(id: string): Observable<any> {
    const endpoint = `${this.baseUrl}/areas/metadata-keys?project_id=${id}`;
    return this.http.get<RiskScores>(endpoint);
  }

  public reorderProject(id: string, body: {after_project_id: string, before_project_id: string}): Observable<any> {
    const endpoint = `${this.baseUrl}/projects/${id}/reorder`;
    return this.http.put<any>(endpoint, body);
  }
}
